/**
 * §A.5 · Los webhooks recibidos.
 *
 * ## «Un webhook de cobro sin procesar es dinero que el club cree que ha entrado»
 *
 * Es la frase del contrato y es toda la razón de esta pantalla. Los pendientes van
 * primero —lo ordena el servidor, no esta tabla— y se entra por «solo los que no se
 * han procesado», porque con miles de eventos correctos ninguno es el que se busca.
 *
 * ## Está vacía, y eso es información
 *
 * H-14 quitó la pasarela, así que hoy no llega ningún webhook. La tabla y las dos
 * rutas existen porque el día que haya pasarela esto es lo que la vigila, y **una
 * pantalla vacía que explica por qué está vacía es mejor que una pantalla que no
 * existe**: sin ella, el día del alta de la pasarela nadie sabría dónde mirar.
 *
 * ## Reprocesar toca la cola de eventos de cobro
 *
 * Por eso lleva `Idempotency-Key` nueva por pulsación y se confirma: reprocesar dos
 * veces el mismo cobro es apuntar dinero que no entró.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import type { Webhook } from '@/backoffice/tipos';

export function Webhooks() {
  const cache = useQueryClient();
  const [soloPendientes, setSoloPendientes] = useState(true);
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const eventos = useQuery({
    queryKey: ['admin', 'webhooks', soloPendientes],
    queryFn: async () => {
      const { data, error } = await api.v1.admin['webhook-events'].get({
        query: {
          ...(soloPendientes ? { only_unprocessed: true } : {}),
          limit: 200,
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Webhook[];
    },
  });

  const reprocesar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1.admin['webhook-events']({ id }).replay.post(
        {},
        // Clave nueva por pulsación: esto toca la cola de eventos de cobro.
        { headers: { 'idempotency-key': crypto.randomUUID() } }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setConfirmando(null);
      void cache.invalidateQueries({ queryKey: ['admin', 'webhooks'] });
    },
    onError: (e) => {
      setConfirmando(null);
      setProblema(aProblema(e));
    },
  });

  return (
    <section>
      <h1>Webhooks</h1>

      <p className="tenue">
        Lo que nos manda el proveedor de cobros.{' '}
        <strong>
          Uno sin procesar es dinero que el club cree que ha entrado
        </strong>
        , así que los pendientes salen primero.
      </p>

      {problema && <PanelError problema={problema} />}

      <div className="filtros">
        <label>
          Qué se ve
          <select
            value={soloPendientes ? 'pendientes' : 'todo'}
            onChange={(e) => setSoloPendientes(e.target.value === 'pendientes')}
          >
            <option value="pendientes">Solo los que faltan por procesar</option>
            <option value="todo">Todos</option>
          </select>
        </label>
      </div>

      {eventos.isPending && (
        <Cargando filas={4} etiqueta="Cargando los webhooks" />
      )}

      {eventos.isError && (
        <PanelError
          problema={aProblema(eventos.error)}
          reintentar={() => void eventos.refetch()}
        />
      )}

      {eventos.data &&
        (eventos.data.length === 0 ? (
          <Vacio
            titulo="No hay ningún webhook"
            explicacion="Hoy no hay pasarela de cobro conectada, así que nadie nos manda eventos. Esta pantalla es la que vigilará la cola el día que la haya: sin ella nadie sabría dónde mirar."
          />
        ) : (
          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Recibido</th>
                  <th>Proveedor</th>
                  <th>Qué es</th>
                  <th>Procesado</th>
                  <th>Si falló, por qué</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {eventos.data.map((e) => (
                  <tr
                    key={e.id}
                    className={e.error ? 'fila-marcada' : undefined}
                  >
                    <td>{fechaYHora(e.received_at)}</td>
                    <td className="tenue">{e.provider}</td>
                    <td>
                      <code>{e.event_type}</code>
                      <br />
                      <span className="tenue">{e.provider_event_id}</span>
                    </td>
                    <td>
                      {e.processed_at ? (
                        fechaYHora(e.processed_at)
                      ) : (
                        <span className="pastilla pastilla-paused">
                          Sin procesar
                        </span>
                      )}
                    </td>
                    <td className="tenue">{e.error ?? '—'}</td>
                    <td>
                      {confirmando === e.id ? (
                        <div className="hueco-acciones">
                          <button
                            type="button"
                            className="btn-plano"
                            disabled={reprocesar.isPending}
                            onClick={() => reprocesar.mutate(e.id)}
                          >
                            Sí, reprocesarlo
                          </button>
                          <button
                            type="button"
                            className="btn-plano"
                            onClick={() => setConfirmando(null)}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn-plano"
                          onClick={() => {
                            setProblema(null);
                            setConfirmando(e.id);
                          }}
                        >
                          Reprocesar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {confirmando && (
        <p className="tenue">
          <strong>
            Reprocesar vuelve a meter el evento en la cola de cobros.
          </strong>{' '}
          Si el primer intento sí llegó a apuntar el cobro, hacerlo otra vez
          apuntaría dinero que no entró.
        </p>
      )}
    </section>
  );
}
